// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The device base — active mechanisms.
//
// A static collider is geometry. A device is a *mechanism*: it has state that changes, it
// reacts to a ball, and it reports what happened as switch events for the rule engine. The
// simulation knows how each kind behaves; the machine definition supplies the numbers
// (04 §7).
//
// Devices participate at four distinct points in a step, and each one is opt-in via a flag
// rather than an always-called empty method. A machine with thirty devices and six balls in
// play would otherwise make 180,000 do-nothing virtual calls a second.

import type { Ball } from '../Ball'
import type { Collider } from '../colliders'
import type { World } from '../World'

export abstract class Device {
  /** Author-facing stable name. Used by the rule engine and in diagnostics. */
  readonly id: string

  /** Kind name, for diagnostics only. Never read in the hot path. */
  abstract readonly kind: string

  /** Index into `World.devices`. Assigned when the world is built. */
  index = -1
  /** Optional explicit collision plane; -1 retains historical device behavior. */
  contactLayer = -1

  /** A disabled device is entirely inert: no forces, no contacts, no events. */
  enabled = true

  /**
   * Whether the static colliders this device owns are currently solid.
   *
   * A dropped target and an open gate are still in the broadphase — rebuilding it would be
   * far more expensive than one array read per candidate — they simply stop answering.
   */
  collidersActive = true

  /** Set by subclasses that need a force applied before the ball integrates. */
  readonly wantsForce: boolean = false
  /** Set by subclasses that resolve their own contacts inside the iteration loop. */
  readonly wantsContact: boolean = false
  /** Set by subclasses that test regions or plane crossings after contacts settle. */
  readonly wantsObserve: boolean = false

  /**
   * Where on the playfield this mechanism sits, metres.
   *
   * Not used by the simulation — a device's geometry is already in its colliders. It exists
   * so that things *about* the machine can ask where a device is: ball search pulsing the
   * coils near a stuck ball, a diagnostic naming what the ball is sitting on, a renderer
   * placing a lamp. Every device sets it; the default is the playfield centre, which is
   * wrong for everything, so a device that forgets is obvious rather than plausible.
   */
  originX = 0
  originY = 0

  constructor(id: string) {
    this.id = id
  }

  /** Set the device's playfield position. Called from each subclass constructor. */
  protected setOrigin(x: number, y: number): void {
    this.originX = x
    this.originY = y
  }

  /**
   * Static colliders this device contributes to the world.
   *
   * Called once, at world construction, before the broadphase is built. Each returned
   * collider must carry this device's index so a hit routes back here.
   */
  colliders(): readonly Collider[] {
    return []
  }

  /** Own dynamics: a spinner spinning down, a gate swinging shut, a hold timer running. */
  step(_world: World, _dt: number): void {}

  /** Force accumulation, before the ball integrates. Magnets, and nothing else so far. */
  applyForce(_world: World, _ball: Ball, _dt: number): void {}

  /** Contact resolution inside the iteration loop. Returns true if the ball was touched. */
  resolveContact(_world: World, _ball: Ball): boolean {
    return false
  }

  /** Region and plane-crossing tests, after contacts have settled for the step. */
  observe(_world: World, _ball: Ball): void {}

  /**
   * One of this device's own static colliders was struck.
   *
   * `nx, ny` is the contact normal pointing away from the surface; `impactSpeed` is the
   * normal approach speed before the bounce, which is what a sound gain and a haptic pulse
   * are actually proportional to.
   */
  onColliderHit(
    _world: World,
    _ball: Ball,
    _nx: number,
    _ny: number,
    _impactSpeed: number,
  ): void {}

  /** Return to the state a fresh ball should find. Called at ball start, not at game start. */
  reset(): void {}

  /**
   * Everything about this mechanism that changes, as plain scalars (M4-28a).
   *
   * The base carries the two flags every device has; a subclass with state of its own overrides
   * `ownState` and `restoreOwn`. Deliberately **not** the configuration — a snapshot restores
   * into a world built from the same definition, and carrying the geometry would mean a restore
   * could quietly reshape the machine.
   *
   * A mechanism whose state is not here is a mechanism that resumes in the wrong position, and
   * the two most likely to be forgotten are the ones that hold something: a scoop with a ball in
   * it and a flipper part way through its stroke. `test/core/snapshot.test.ts` compares every
   * scalar field of every object after a restore, so forgetting one fails rather than lurking.
   */
  snapshot(): DeviceState {
    return { enabled: this.enabled, collidersActive: this.collidersActive, ...this.ownState() }
  }

  restore(state: DeviceState): void {
    this.enabled = state['enabled'] === true
    this.collidersActive = state['collidersActive'] === true
    this.restoreOwn(state)
  }

  protected ownState(): DeviceState {
    return {}
  }

  protected restoreOwn(_state: DeviceState): void {}
}

/**
 * One mechanism's mutable state.
 *
 * A flat bag of scalars rather than a typed record per device kind. Twelve device kinds would
 * otherwise mean twelve interfaces, twelve type guards and a discriminant to keep in step with
 * `kind` — and the consumer is a checksum and a restore, neither of which reads a field by name.
 */
export type DeviceState = Readonly<Record<string, number | boolean | readonly number[]>>
